import { useEffect, useState } from "react";
import { Link, Navigate } from "react-router-dom";
import { Check, ClipboardCheck, Copy, FileSearch, Home, MessageCircle, Tag } from "lucide-react";
import { useSEO } from "../lib/seo";
import { trackEvent } from "../lib/api";
import { COMPANY } from "../lib/constants";
import { QUOTE_DONE_KEY } from "../components/sections/ProjectAdvies";

const NEXT_STEPS = [
  { icon: ClipboardCheck, title: "Aanvraag ontvangen", text: "Uw gegevens en wensen zijn veilig ontvangen." },
  { icon: FileSearch, title: "Persoonlijke beoordeling", text: "Wij bekijken uw aanvraag en eventuele foto's om de mogelijkheden te beoordelen." },
  { icon: Tag, title: "Prijsindicatie", text: "U ontvangt een persoonlijke reactie met een richtprijs of aanvullende vragen." },
];

const readDone = () => {
  try { return JSON.parse(sessionStorage.getItem(QUOTE_DONE_KEY) || "null"); } catch { return null; }
};

export const RequestNumber = ({ number }) => {
  const [copied, setCopied] = useState(false);
  const copy = () => navigator.clipboard?.writeText(number).then(() => { setCopied(true); setTimeout(() => setCopied(false), 2000); }).catch(() => {});
  return (
    <div className="mt-8 flex flex-col items-center gap-3 rounded-2xl bg-petrol px-5 py-5 text-white sm:flex-row sm:justify-between sm:px-7" data-testid="thanks-request-number-box">
      <div className="text-center sm:text-left">
        <p className="text-[12px] font-semibold uppercase tracking-[0.16em] text-[#D9B97A]">Uw aanvraagnummer</p>
        <p className="mt-1 font-mono text-[26px] font-bold tracking-wide sm:text-[30px]" data-testid="thanks-request-number">{number}</p>
      </div>
      <button type="button" onClick={copy} data-testid="thanks-copy-number"
        className="inline-flex min-h-[44px] items-center gap-2 rounded-xl border border-white/25 bg-white/10 px-4 text-[14px] font-semibold transition-colors hover:bg-white/15">
        {copied ? <><Check className="h-4 w-4" aria-hidden="true" /> Gekopieerd</> : <><Copy className="h-4 w-4" aria-hidden="true" /> Kopieer nummer</>}
      </button>
    </div>
  );
};

const StepsList = () => (
  <ol className="mt-10 grid gap-4 md:grid-cols-3" data-testid="thanks-next-steps">
    {NEXT_STEPS.map(({ icon: Icon, title, text }, i) => (
      <li key={title} className={`reveal relative rounded-2xl border p-5 ${i === 0 ? "border-maas/30 bg-maas-soft/60" : "border-slate-200 bg-white"}`} style={{ animationDelay: `${120 + i * 90}ms` }}>
        <div className="flex items-center gap-3">
          <span className={`flex h-10 w-10 items-center justify-center rounded-xl ${i === 0 ? "bg-maas text-white" : "bg-slate-100 text-ink"}`}><Icon className="h-5 w-5" aria-hidden="true" /></span>
          <span className="text-[12px] font-semibold uppercase tracking-[0.14em] text-gold">Stap {i + 1}</span>
          {i === 0 && <Check className="ml-auto h-5 w-5 text-maas" aria-label="Afgerond" />}
        </div>
        <h3 className="mt-4 text-[16.5px] font-bold text-ink">{title}</h3>
        <p className="mt-1.5 text-[14.5px] leading-relaxed text-slate-600">{text}</p>
      </li>
    ))}
  </ol>
);

export default function OfferteBedankt() {
  const [done] = useState(readDone);
  useSEO({ title: "Bedankt voor uw aanvraag | Maas Kozijn", description: "Uw aanvraag bij Maas Kozijn is ontvangen.", path: "/offerte-bedankt", noindex: true });

  useEffect(() => {
    const cur = readDone();
    if (!cur || cur.tracked) return;
    sessionStorage.setItem(QUOTE_DONE_KEY, JSON.stringify({ ...cur, tracked: true }));
    trackEvent("generate_lead", { lead_type: "quote_request", product_category: cur.product_category, currency: "EUR", value: 1 });
  }, []);

  if (!done?.lead_number) return <Navigate to="/offerte-aanvragen" replace />;
  const waText = `Goedendag Maas Kozijn, ik heb zojuist een aanvraag gedaan (aanvraagnummer ${done.lead_number}). Ik heb nog een vraag / stuur graag aanvullende foto's.`;
  const waLink = `https://api.whatsapp.com/send/?phone=${COMPANY.whatsappDigits}&text=${encodeURIComponent(waText)}`;

  return (
    <section className="bg-slate-50" data-testid="thanks-page">
      <div className="container-tight max-w-4xl py-10 sm:py-16">
        <div className="reveal rounded-3xl border border-slate-200 bg-white p-6 text-center sm:p-10" style={{ boxShadow: "var(--shadow-2)" }}>
          <span className="mx-auto flex h-16 w-16 items-center justify-center rounded-full bg-emerald-50 ring-8 ring-emerald-50/60" data-testid="thanks-check">
            <span className="flex h-11 w-11 items-center justify-center rounded-full bg-emerald-600 text-white"><Check className="h-6 w-6" strokeWidth={3} aria-hidden="true" /></span>
          </span>
          <h1 className="mt-6 text-[clamp(1.75rem,2.4vw+1rem,2.6rem)] font-bold leading-tight text-ink" data-testid="thanks-title">Bedankt voor uw aanvraag!</h1>
          <p className="mx-auto mt-4 max-w-2xl text-[16px] leading-relaxed text-slate-600 sm:text-[17px]">
            Uw aanvraag voor nieuwe kozijnen is succesvol ontvangen. Wij bekijken uw wensen en nemen zo spoedig mogelijk persoonlijk contact met u op.
          </p>
          <RequestNumber number={done.lead_number} />
          <p className="mt-3 text-[13px] text-slate-500">Vermeld dit nummer als u contact met ons opneemt over uw aanvraag.</p>
        </div>
        <h2 className="mt-12 text-[20px] font-bold text-ink sm:text-[22px]">Wat gebeurt er nu?</h2>
        <StepsList />
        <div className="mt-10 flex flex-col items-center gap-4 rounded-3xl border border-slate-200 bg-white p-6 text-center sm:p-8" data-testid="thanks-contact-block">
          <p className="text-[17px] font-semibold text-ink">Heeft u nog vragen of wilt u aanvullende foto's sturen?</p>
          <div className="flex w-full flex-col gap-3 sm:w-auto sm:flex-row">
            <a href={waLink} target="_blank" rel="noopener noreferrer" onClick={() => trackEvent("click_whatsapp", { page_type: "thank_you" })}
              className="btn bg-[#1F8F4E] text-white hover:bg-[#18733F] min-h-[52px] px-6" data-testid="thanks-whatsapp">
              <MessageCircle className="h-5 w-5" aria-hidden="true" /> Stel uw vraag via WhatsApp
            </a>
            <Link to="/" className="btn-secondary min-h-[52px] px-6" data-testid="thanks-home"><Home className="h-5 w-5" aria-hidden="true" /> Terug naar de homepage</Link>
          </div>
        </div>
      </div>
    </section>
  );
}
